import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingCart, ZoomIn, ZoomOut, Printer, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/RegionContext';
import { Button } from '@/components/ui/button';
import ShareButtons from '@/components/ShareButtons';
import WishlistButton from '@/components/WishlistButton';
import ReviewSection from '@/components/ReviewSection';
import ProgressiveImage from '@/components/ProgressiveImage';
import { trackAddToCart, trackViewContent } from '@/components/TrackingScripts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const ArtworkModal = ({ artwork, isOpen, onClose }) => {
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const [isZoomed, setIsZoomed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [similarArtworks, setSimilarArtworks] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && artwork?.id) {
      axios.get(`${API}/artworks/${artwork.id}/similar?limit=4`)
        .then(res => setSimilarArtworks(res.data))
        .catch(() => setSimilarArtworks([]));
    } else {
      setSimilarArtworks([]);
    }
  }, [isOpen, artwork?.id]);

  // Track view content when modal opens
  useEffect(() => {
    if (isOpen && artwork) trackViewContent(artwork);
  }, [isOpen, artwork]);

  if (!isOpen || !artwork) return null;

  const handleAddToCart = () => {
    addToCart({
      id: artwork.id,
      title: artwork.title,
      price: artwork.price || (artwork.art_type === 'Digital' ? 5000 : 10000),
      image_url: artwork.image_url,
      art_type: artwork.art_type,
      category: artwork.category,
    });
    trackAddToCart(artwork);
    toast.success(`"${artwork.title}" added to cart`);
  };

  const price = artwork.price || (artwork.art_type === 'Digital' ? 5000 : 10000);

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const handleDigitalDownload = async () => {
    setIsProcessing(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error('Payment gateway failed to load'); setIsProcessing(false); return; }
      const customerName = prompt('Your Name:');
      if (!customerName) { setIsProcessing(false); return; }
      const customerEmail = prompt('Your Email:');
      if (!customerEmail) { setIsProcessing(false); return; }

      const res = await axios.post(`${API}/digital-orders/create`, {
        artwork_id: artwork.id, customer_name: customerName, customer_email: customerEmail
      });

      const options = {
        key: RAZORPAY_KEY,
        amount: res.data.amount * 100,
        currency: 'INR',
        name: 'Gangadhar Som - Digital Art',
        description: `Digital Download: ${artwork.title}`,
        order_id: res.data.razorpay_order_id,
        handler: async (resp) => {
          try {
            const verifyRes = await axios.post(`${API}/orders/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature
            });
            if (verifyRes.data.success && verifyRes.data.download_token) {
              toast.success('Payment successful! Starting download...');
              window.open(`${API}/downloads/${verifyRes.data.download_token}`, '_blank');
            } else {
              toast.success('Payment verified! Check your email for the download link.');
            }
          } catch { toast.error('Payment verification failed'); }
        },
        prefill: { name: customerName, email: customerEmail },
        theme: { color: '#D97706' },
        modal: { ondismiss: () => { setIsProcessing(false); toast.error('Payment cancelled'); } }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      console.error(e);
      toast.error('Failed to create order');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSimilarClick = (similar) => {
    // Navigate to gallery with the artwork selected
    onClose();
    navigate(`/gallery?artwork=${similar.id}`);
  };

  return (
    <AnimatePresence>
      {/* Zoomed Image Overlay */}
      {isZoomed && (
        <motion.div
          key="zoom-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black flex items-center justify-center p-2"
          onClick={() => setIsZoomed(false)}
        >
          <button className="absolute top-4 right-4 p-2 text-white/80 hover:text-white z-10" onClick={() => setIsZoomed(false)}>
            <ZoomOut size={28} />
          </button>
          <img
            src={artwork.image_url} alt={artwork.title}
            className="max-w-full max-h-full object-contain zoomed-image pointer-events-none select-none"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
            style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
          />
          <div className="absolute inset-0" />
        </motion.div>
      )}

      {/* Modal */}
      <motion.div
        key="modal-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/90"
        onClick={onClose} data-testid="artwork-modal"
      >
        <button data-testid="close-modal-button" onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-white hover:text-accent transition-colors z-10">
          <X size={28} />
        </button>

        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-6xl sm:mx-4 bg-card sm:rounded-lg overflow-hidden max-h-[92vh] sm:max-h-[85vh] overflow-y-auto"
        >
          <div className="flex flex-col sm:grid sm:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative bg-background flex items-center justify-center p-3 sm:p-6 sm:min-h-[400px] protected-image select-none">
              <img
                src={artwork.image_url} alt={artwork.title}
                className="max-w-full max-h-[50vh] sm:max-h-[75vh] object-contain zoom-cursor pointer-events-none select-none"
                data-testid="modal-artwork-image"
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
                style={{ WebkitUserDrag: 'none', userSelect: 'none' }}
              />
              <div className="absolute inset-0 z-[1] cursor-zoom-in" onClick={() => setIsZoomed(true)} />
              <button
                onClick={() => setIsZoomed(true)}
                className="absolute bottom-3 right-3 p-2 bg-black/60 text-white rounded-full hover:bg-black/80 transition-colors z-[2]"
                data-testid="zoom-button"
              >
                <ZoomIn size={18} />
              </button>
            </div>

            {/* Details */}
            <div className="p-5 sm:p-8 md:p-12 space-y-4 sm:space-y-6 overflow-y-auto">
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light mb-1" data-testid="modal-artwork-title">
                  {artwork.title}
                </h2>
                {artwork.year && (
                  <p className="font-sans text-muted-foreground text-sm" data-testid="modal-artwork-year">{artwork.year}</p>
                )}
              </div>

              {artwork.description && (
                <p className="font-sans text-sm sm:text-base leading-relaxed text-foreground/80" data-testid="modal-artwork-description">
                  {artwork.description}
                </p>
              )}

              {artwork.tags && artwork.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="modal-artwork-tags">
                  {artwork.tags.map((tag, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-accent/10 text-accent rounded-full font-sans">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="space-y-2.5 pt-3 border-t border-border">
                {[
                  { label: 'Medium', value: artwork.medium, testid: 'modal-artwork-medium' },
                  { label: 'Dimensions', value: artwork.dimensions, testid: 'modal-artwork-dimensions' },
                  { label: 'Category', value: artwork.category, testid: 'modal-artwork-category' },
                  { label: 'Type', value: artwork.art_type, testid: 'modal-artwork-type' },
                ].map(({ label, value, testid }) => value ? (
                  <div key={label}>
                    <span className="font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs text-muted-foreground">{label}</span>
                    <p className="font-sans text-sm sm:text-base mt-0.5" data-testid={testid}>{value}</p>
                  </div>
                ) : null)}
              </div>

              {/* Price & Add to Cart */}
              {artwork.for_sale !== false ? (
                <div className="pt-4 border-t border-border space-y-3">
                  <p className="font-serif text-2xl sm:text-3xl font-light" data-testid="modal-artwork-price">
                    {formatPrice(price)}
                  </p>
                  <Button
                    onClick={handleAddToCart} data-testid="modal-add-to-cart-button"
                    className="w-full bg-accent hover:bg-accent/90 text-white active:scale-[0.98] transition-transform"
                  >
                    <ShoppingCart size={18} className="mr-2" /> Buy Original
                  </Button>
                  <Button
                    onClick={() => { onClose(); navigate(`/print/${artwork.id}`); }}
                    data-testid="modal-order-print-button"
                    variant="outline"
                    className="w-full border-accent text-accent hover:bg-accent hover:text-white"
                  >
                    <Printer size={18} className="mr-2" /> Order Print
                  </Button>
                  <Button
                    onClick={handleDigitalDownload}
                    disabled={isProcessing}
                    data-testid="modal-digital-download-button"
                    variant="outline"
                    className="w-full border-violet-500 text-violet-600 hover:bg-violet-500 hover:text-white"
                  >
                    <Download size={18} className="mr-2" /> {isProcessing ? 'Processing...' : `Buy Digital Download — ${formatPrice(499)}`}
                  </Button>
                  <p className="text-[10px] sm:text-xs text-muted-foreground text-center">
                    Prints available in multiple sizes · Digital downloads in high resolution
                  </p>
                </div>
              ) : (
                <div className="pt-4 border-t border-border space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-3 py-1 bg-red-100 text-red-700 font-sans uppercase tracking-wider text-xs rounded-full font-medium" data-testid="modal-sold-badge">Sold</span>
                  </div>
                  <Button
                    onClick={() => { onClose(); navigate(`/print/${artwork.id}`); }}
                    data-testid="modal-order-print-button"
                    variant="outline"
                    className="w-full border-accent text-accent hover:bg-accent hover:text-white"
                  >
                    <Printer size={18} className="mr-2" /> Order Print
                  </Button>
                  <Button
                    onClick={handleDigitalDownload}
                    disabled={isProcessing}
                    data-testid="modal-digital-download-button"
                    variant="outline"
                    className="w-full border-violet-500 text-violet-600 hover:bg-violet-500 hover:text-white"
                  >
                    <Download size={18} className="mr-2" /> {isProcessing ? 'Processing...' : `Buy Digital Download — ${formatPrice(499)}`}
                  </Button>
                  <p className="text-[10px] sm:text-xs text-muted-foreground text-center">
                    Original sold · Prints and digital downloads still available
                  </p>
                </div>
              )}

              {/* Share & Wishlist */}
              <div className="pt-3 border-t border-border flex items-start gap-3">
                <div className="flex-1"><ShareButtons artwork={artwork} /></div>
                <WishlistButton artworkId={artwork.id} />
              </div>

              {/* Similar Artworks */}
              {similarArtworks.length > 0 && (
                <div className="pt-4 border-t border-border" data-testid="similar-artworks-section">
                  <h3 className="font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs text-muted-foreground mb-3">
                    You May Also Like
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {similarArtworks.map((similar) => (
                      <button
                        key={similar.id}
                        onClick={() => handleSimilarClick(similar)}
                        className="group relative overflow-hidden rounded-md aspect-square bg-muted cursor-pointer"
                        data-testid={`similar-artwork-${similar.id}`}
                      >
                        <ProgressiveImage
                          src={similar.thumb_url || similar.image_url}
                          placeholder={similar.placeholder}
                          alt={similar.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-2">
                          <span className="text-white text-xs font-sans truncate">{similar.title}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Buyer Reviews */}
              <ReviewSection artworkId={artwork.id} artworkTitle={artwork.title} />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ArtworkModal;
